import {
  Form,
  FormField,
  type FormControl,
  type FormSubmitHandler,
  type NavigateWithoutBlocker,
  type Option,
  Separator,
} from 'platform/components';
import {Grid, GridItem, Text, HStack, VStack} from 'platform/foundation';

import type {UseFormReturn} from 'react-hook-form';
import {Trans} from 'react-i18next';

import type {InterestAssignmentConditionResponseBody} from '@dms/api/core';
import {i18n} from '@dms/i18n';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';

import {useAssignmentConditionFormOptions} from './hooks/useAssignmentConditionFormOptions';
import {useAssignmentConditionVehicleOptions} from './hooks/useAssignmentConditionVehicleOptions';
import {useResetVehicleDependentFields} from './hooks/useResetVehicleDependentFields';
import {
  assignmentConditionFormSchema,
  type AssignmentConditionFormValues,
  responseToFormValues,
} from './schemas/assignmentConditionFormSchema';

const CONDITIONS_TITLE_KEY = 'entity.interest.labels.conditions';
const CONDITIONS_DESCRIPTION_KEY = 'entity.interest.labels.conditionsDescription';
const RULE_NAME_KEY = 'entity.interest.labels.ruleName';
const TARGET_TEAM_KEY = 'entity.interest.labels.targetTeam';
const BRANCH_KEY = 'entity.branch.labels.branch';
const LEAD_SOURCE_KEY = 'entity.interest.labels.leadSource';
const VEHICLE_TYPE_KEY = 'entity.vehicle.labels.type';
const VEHICLE_CONDITION_KEY = 'entity.vehicle.labels.vehicleCondition';
const MAKE_KEY = 'entity.vehicle.labels.make';
const MODEL_KEY = 'entity.vehicle.labels.model';
const MODEL_FAMILY_KEY = 'entity.vehicle.labels.modelFamily';
const FUEL_KEY = 'entity.vehicle.labels.fuel';
const MILEAGE_FROM_KEY = 'entity.vehicle.labels.mileageFrom';
const MILEAGE_TO_KEY = 'entity.vehicle.labels.mileageTo';

const TOOLTIP_SELECT_MAKE = 'entity.interest.tooltips.selectMake';
const TOOLTIP_SELECT_MODEL = 'entity.interest.tooltips.selectModel';
const TOOLTIP_SELECT_MODEL_FAMILY = 'entity.interest.tooltips.selectModelFamily';

export interface AssignmentConditionFormProps {
  baseTestId: (suffix: string) => string;
  initialData?: InterestAssignmentConditionResponseBody;
  onSubmit: (
    values: AssignmentConditionFormValues,
    blockerContext?: {wasSubmittedFromBlocker?: boolean; reportApiFailure?: VoidFunction}
  ) => Promise<void>;
  onDiscard: (navigateWithoutBlocker: NavigateWithoutBlocker) => void;
  teamOptions?: Option[];
}

const DEFAULT_FORM_VALUES: Partial<AssignmentConditionFormValues> = {
  name: '',
  teamId: '',
  branchId: [],
  make: null,
  model: null,
  modelFamily: null,
  condition: null,
  fuel: null,
  type: null,
  sourceId: [],
  mileageFrom: null,
  mileageTo: null,
};

export function AssignmentConditionForm(props: AssignmentConditionFormProps) {
  const defaultValues: Partial<AssignmentConditionFormValues> = props.initialData
    ? responseToFormValues(props.initialData)
    : DEFAULT_FORM_VALUES;

  const handleSubmit: FormSubmitHandler<AssignmentConditionFormValues> = (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => props.onSubmit(values, blockerContext);

  return (
    <SettingsSection>
      <Form<AssignmentConditionFormValues>
        experimentalZodSchema={assignmentConditionFormSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        shouldWatchForUnsavedChanges
        data-testid={props.baseTestId('form')}
      >
        {(control, formApi, navigateWithoutBlocker) => (
          <AssignmentConditionFormContent
            control={control}
            formApi={formApi}
            baseTestId={props.baseTestId}
            onDiscard={props.onDiscard}
            navigateWithoutBlocker={navigateWithoutBlocker}
            teamOptions={props.teamOptions}
          />
        )}
      </Form>
    </SettingsSection>
  );
}

interface AssignmentConditionFormContentProps {
  control: FormControl<AssignmentConditionFormValues>;
  formApi: UseFormReturn<AssignmentConditionFormValues>;
  baseTestId: (suffix: string) => string;
  onDiscard: (navigateWithoutBlocker: NavigateWithoutBlocker) => void;
  navigateWithoutBlocker: NavigateWithoutBlocker;
  teamOptions?: Option[];
}

function AssignmentConditionFormContent(props: AssignmentConditionFormContentProps) {
  const baseOptions = useAssignmentConditionFormOptions();
  const vehicleOptions = useAssignmentConditionVehicleOptions(props.control);
  const teamOptions = props.teamOptions ?? baseOptions.teamOptions;
  const {onVehicleTypeChange, onMakeChange} = useResetVehicleDependentFields(
    props.formApi,
    vehicleOptions.getVehicleLookupMaps
  );

  const makeFieldTooltip = vehicleOptions.isMakeDisabled ? i18n.t(TOOLTIP_SELECT_MAKE) : undefined;
  const modelFieldTooltip = vehicleOptions.isModelDisabled
    ? i18n.t(TOOLTIP_SELECT_MODEL)
    : undefined;
  const modelFamilyFieldTooltip = vehicleOptions.isModelFamilyDisabled
    ? i18n.t(TOOLTIP_SELECT_MODEL_FAMILY)
    : undefined;
  const fuelFieldTooltip = vehicleOptions.isFuelDisabled ? i18n.t(TOOLTIP_SELECT_MAKE) : undefined;

  return (
    <VStack spacing={6}>
      <Grid columns={2}>
        <GridItem>
          <FormField
            control={props.control}
            name="name"
            type="text"
            label={i18n.t(RULE_NAME_KEY)}
            data-testid={props.baseTestId('ruleName')}
            isRequired
          />
        </GridItem>
        <GridItem>
          <FormField
            control={props.control}
            name="teamId"
            type="choice"
            label={i18n.t(TARGET_TEAM_KEY)}
            options={teamOptions}
            placeholder={i18n.t('general.labels.selectOption')}
            data-testid={props.baseTestId('teamId')}
            isRequired
          />
        </GridItem>
      </Grid>

      <Separator spacing={1} />

      <VStack spacing={4}>
        <VStack spacing={2}>
          <Text alternative>{i18n.t(CONDITIONS_TITLE_KEY)}</Text>
          <Text size="small">
            <Trans i18nKey={CONDITIONS_DESCRIPTION_KEY} components={{break: <br />}} />
          </Text>
        </VStack>

        <Grid columns={2}>
          <GridItem>
            <FormField
              control={props.control}
              name="branchId"
              type="multiChoice"
              label={i18n.t(BRANCH_KEY)}
              options={baseOptions.branchOptions}
              placeholder={i18n.t('general.labels.selectOption')}
              data-testid={props.baseTestId('branchId')}
            />
          </GridItem>
          <GridItem>
            <FormField
              control={props.control}
              name="sourceId"
              type="multiChoice"
              label={i18n.t(LEAD_SOURCE_KEY)}
              options={baseOptions.leadSourceOptions}
              placeholder={i18n.t('general.labels.selectOption')}
              data-testid={props.baseTestId('sourceId')}
            />
          </GridItem>
          <GridItem>
            <FormField
              control={props.control}
              name="type"
              type="choice"
              label={i18n.t(VEHICLE_TYPE_KEY)}
              options={baseOptions.vehicleTypeOptions}
              placeholder={i18n.t('general.labels.selectOption')}
              data-testid={props.baseTestId('vehicleType')}
              onChange={onVehicleTypeChange}
            />
          </GridItem>
          <GridItem>
            <FormField
              control={props.control}
              name="condition"
              type="multiChoice"
              label={i18n.t(VEHICLE_CONDITION_KEY)}
              options={baseOptions.vehicleConditionOptions}
              placeholder={i18n.t('general.labels.selectOption')}
              data-testid={props.baseTestId('vehicleCategory')}
            />
          </GridItem>
          <GridItem>
            <FormField
              control={props.control}
              name="make"
              type="multiChoice"
              label={i18n.t(MAKE_KEY)}
              options={vehicleOptions.makeOptions}
              placeholder={i18n.t('general.labels.selectOption')}
              data-testid={props.baseTestId('make')}
              isLoading={vehicleOptions.isMakeModelLoading}
              isDisabled={vehicleOptions.isMakeDisabled}
              tooltip={makeFieldTooltip}
              onChange={onMakeChange}
            />
          </GridItem>
          <GridItem>
            <FormField
              control={props.control}
              name="model"
              type="multiChoice"
              label={i18n.t(MODEL_KEY)}
              options={vehicleOptions.modelOptionsGroupedByMake}
              placeholder={i18n.t('general.labels.selectOption')}
              data-testid={props.baseTestId('model')}
              isLoading={vehicleOptions.isMakeModelLoading}
              isDisabled={vehicleOptions.isModelDisabled}
              tooltip={modelFieldTooltip}
            />
          </GridItem>
          <GridItem>
            <FormField
              control={props.control}
              name="modelFamily"
              type="multiChoice"
              label={i18n.t(MODEL_FAMILY_KEY)}
              options={vehicleOptions.modelFamilyOptionsGroupedByMake}
              placeholder={i18n.t('general.labels.selectOption')}
              data-testid={props.baseTestId('modelFamily')}
              isLoading={vehicleOptions.isMakeModelLoading}
              isDisabled={vehicleOptions.isModelFamilyDisabled}
              tooltip={modelFamilyFieldTooltip}
            />
          </GridItem>
          <GridItem>
            <FormField
              control={props.control}
              name="fuel"
              type="multiChoice"
              label={i18n.t(FUEL_KEY)}
              options={vehicleOptions.fuelOptions}
              placeholder={i18n.t('general.labels.selectOption')}
              isDisabled={vehicleOptions.isFuelDisabled}
              isLoading={vehicleOptions.isFuelLoading}
              tooltip={fuelFieldTooltip}
              data-testid={props.baseTestId('fuel')}
            />
          </GridItem>
          <GridItem>
            <HStack spacing={4}>
              <FormField
                control={props.control}
                name="mileageFrom"
                type="integer"
                label={i18n.t(MILEAGE_FROM_KEY)}
                data-testid={props.baseTestId('mileageFrom')}
                suffix="km"
              />
              <FormField
                control={props.control}
                name="mileageTo"
                type="integer"
                label={i18n.t(MILEAGE_TO_KEY)}
                data-testid={props.baseTestId('mileageTo')}
                suffix="km"
              />
            </HStack>
          </GridItem>
        </Grid>
      </VStack>

      <SettingsFooter
        actions={[
          {
            type: 'button',
            title: i18n.t('general.actions.discardChanges'),
            variant: 'secondary',
            onClick: () => props.onDiscard(props.navigateWithoutBlocker),
            'data-testid': props.baseTestId('discard'),
          },
          {
            type: 'form-button',
            control: props.control,
            title: i18n.t('general.actions.saveAndClose'),
            buttonType: 'submit',
            'data-testid': props.baseTestId('submit'),
          },
        ]}
      />
    </VStack>
  );
}
